// compressAudio.js — transparent client-side audio compression for upload.
//
// Strips video, downmixes to mono, resamples to 16 kHz, re-encodes to MP3 at
// 48 kbps using ffmpeg.wasm (single-threaded build — no SharedArrayBuffer /
// cross-origin isolation required). A 40 MB / 29-min sales call becomes
// ~5 MB so Whisper has less audio to chew on.
//
// The ffmpeg.wasm core is lazy-loaded from unpkg (no bundler pre-fetch) and
// the instance is cached per session so subsequent compressions are fast.

const FFMPEG_CORE_VERSION = '0.12.6';
const CORE_BASE_URL = `https://unpkg.com/@ffmpeg/core@${FFMPEG_CORE_VERSION}/dist/umd`;

const AV_EXT_RE = /\.(mp4|mov|webm|m4a|mp3|wav|ogg|flac)$/i;
const AV_MIME_RE = /^(audio|video)\//;

const SKIP_COMPRESSION_BYTES = 5 * 1024 * 1024;   // already small audio → upload as-is
const REJECT_BYTES           = 200 * 1024 * 1024; // browser can't realistically handle bigger

export function isAudioOrVideo(file) {
  if (!file) return false;
  if (AV_MIME_RE.test(file.type || '')) return true;
  return AV_EXT_RE.test(file.name || '');
}

let ffmpegInstance = null;
let ffmpegLoadPromise = null;

async function getFfmpeg() {
  if (ffmpegInstance) return ffmpegInstance;
  if (ffmpegLoadPromise) return ffmpegLoadPromise;

  ffmpegLoadPromise = (async () => {
    const { FFmpeg } = await import('@ffmpeg/ffmpeg');
    const { toBlobURL } = await import('@ffmpeg/util');
    const ff = new FFmpeg();
    await ff.load({
      coreURL: await toBlobURL(`${CORE_BASE_URL}/ffmpeg-core.js`, 'text/javascript'),
      wasmURL: await toBlobURL(`${CORE_BASE_URL}/ffmpeg-core.wasm`, 'application/wasm'),
    });
    ffmpegInstance = ff;
    return ff;
  })();

  try {
    return await ffmpegLoadPromise;
  } finally {
    ffmpegLoadPromise = null;
  }
}

// Exposed for tests so the cached instance can be reset between runs.
export function __resetFfmpegForTests() {
  ffmpegInstance = null;
  ffmpegLoadPromise = null;
}

export async function compressAudioFile(file, { onProgress } = {}) {
  const started = Date.now();
  const { fetchFile } = await import('@ffmpeg/util');
  const ff = await getFfmpeg();

  const inExt = (file.name && file.name.split('.').pop()) || 'bin';
  const stamp = Date.now() + '_' + Math.random().toString(36).slice(2, 8);
  const inName = `in_${stamp}.${inExt}`;
  const outName = `out_${stamp}.mp3`;

  let progressHandler = null;
  if (onProgress) {
    progressHandler = ({ progress }) => {
      try { onProgress(Math.max(0, Math.min(1, progress))); } catch (_) { /* noop */ }
    };
    ff.on('progress', progressHandler);
  }

  try {
    await ff.writeFile(inName, await fetchFile(file));
    await ff.exec([
      '-i', inName,
      '-vn',                 // drop any video track
      '-ac', '1',            // mono
      '-ar', '16000',        // 16 kHz — Whisper's native rate
      '-c:a', 'libmp3lame',
      '-b:a', '48k',
      outName,
    ]);
    const data = await ff.readFile(outName);
    const blob = new Blob([data.buffer || data], { type: 'audio/mpeg' });
    const baseName = ((file.name || 'recording').replace(/\.[^.]+$/, '') || 'recording') + '.mp3';
    const out = new File([blob], baseName, { type: 'audio/mpeg' });
    return {
      blob,
      file: out,
      originalSize: file.size,
      compressedSize: out.size,
      durationMs: Date.now() - started,
    };
  } finally {
    if (progressHandler) {
      try { ff.off('progress', progressHandler); } catch (_) { /* noop */ }
    }
    try { await ff.deleteFile(inName); } catch (_) { /* noop */ }
    try { await ff.deleteFile(outName); } catch (_) { /* noop */ }
  }
}

// Decide what to do with a candidate upload. Returns one of:
//   { action: 'passthrough' }                     — not audio/video, or already small
//   { action: 'reject', reason }                  — over the hard size cap
//   { action: 'compress' }                        — caller should run compressAudioFile
export function planCompression(file) {
  if (!isAudioOrVideo(file)) return { action: 'passthrough' };
  if (file.size > REJECT_BYTES) {
    return { action: 'reject', reason: 'Recording too large — please trim to under 200 MB and try again' };
  }
  if (file.size <= SKIP_COMPRESSION_BYTES) return { action: 'passthrough' };
  return { action: 'compress' };
}

// Format helper so callers all show the same compact "40.2 MB → 5.1 MB (87% smaller)".
export function formatCompressionSummary(originalSize, compressedSize) {
  const fmt = (n) => `${(n / 1e6).toFixed(1)} MB`;
  const pct = originalSize > 0
    ? Math.max(0, Math.round((1 - compressedSize / originalSize) * 100))
    : 0;
  return `Compressed ${fmt(originalSize)} → ${fmt(compressedSize)} (${pct}% smaller)`;
}

export const __testing__ = {
  AV_EXT_RE,
  AV_MIME_RE,
  SKIP_COMPRESSION_BYTES,
  REJECT_BYTES,
};
